import { c as _c } from "react/compiler-runtime";
import { FormInput, FormSelect, LocaleEnum, PrimaryButton, getLocaleLanguage, useTranslation as useUiTranslation } from '@systemeio/ui';
import { updateBlog } from 'modules/blogs/api/blog-api';
import { BlogApiInterface, BlogEditInterface, BlogErrorsInterface, BlogInterface } from 'modules/blogs/types/blog-interface';
import React, { useState } from 'react';
import EditModal from 'shared/components/edit-modal';
import FormUrlPathInput from 'shared/components/form-url-path-input';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useMutationUpdate } from 'shared/hooks/use-mutation-update';
import { UpdateDataFunctionType } from 'shared/hooks/use-update-data';
import useUserDomains from 'shared/hooks/use-user-domains';
import { LocaleDataInterface } from 'shared/types/locale-data-interface';
import { fetchWithErrors } from 'shared/utils/fetch-with-errors';
import { KeyedMutator } from 'swr';
import { RssIcon, SettingsIcon } from '@systemeio/ui/icons';
interface BlogSettingsProps {
  blog?: BlogInterface;
  updateData: UpdateDataFunctionType;
  blogMutate: KeyedMutator<BlogInterface>;
}
const defaultErrors: BlogErrorsInterface = {
  name: '',
  path: '',
  domain: '',
  locale: '',
  template: ''
};
const BlogSettings = t0 => {
  const $ = _c(28);
  const {
    blog,
    updateData,
    blogMutate
  } = t0;
  const mutationUpdate = useMutationUpdate();
  const {
    t
  } = useLocoTranslation();
  const {
    t: tUi
  } = useUiTranslation();
  const [errors, setErrors] = useState(defaultErrors);
  const [settingsOpened, setSettingsOpened] = useState(false);
  const [shouldFetchDomains, setShouldFetchDomains] = useState(false);
  const {
    userDomainsSelect
  } = useUserDomains(shouldFetchDomains);
  let t1;
  if ($[0] !== tUi) {
    t1 = Object.values(LocaleEnum).map(locale => ({
      id: locale as LocaleEnum,
      caption: getLocaleLanguage(locale, tUi)
    }));
    $[0] = tUi;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const localeData = t1;
  let t2;
  if ($[2] !== blog || $[3] !== blogMutate || $[4] !== mutationUpdate || $[5] !== updateData) {
    t2 = async data => {
      if (blog) {
        await fetchWithErrors(async () => {
          const newData = {
            name: data.name,
            domain: data.domain.id,
            path: data.path,
            locale: data.locale
          };
          const response = await updateBlog(blog.id, newData);
          await updateData(false, undefined, {
            mutationData: blog,
            onMutate: async () => {
              await blogMutate(() => ({
                ...response.data
              }), false);
            }
          }, {
            id: blog?.id,
            onMutate: async apiKey => {
              await mutationUpdate(apiKey, data_0 => data_0 && {
                hasMore: data_0.hasMore,
                items: data_0.items.map(item => {
                  if (item.id === response.data.id) {
                    item = response.data;
                  }
                  return item;
                })
              });
            }
          });
        }, setErrors);
      }
    };
    $[2] = blog;
    $[3] = blogMutate;
    $[4] = mutationUpdate;
    $[5] = updateData;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const onEditBlog = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setErrors(defaultErrors);
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const onModalClose = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setSettingsOpened(true);
      setShouldFetchDomains(true);
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = !blog;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SettingsIcon className="fill-background-secondary" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t("dashboard.blog.blog_settings");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <span className="hidden lg:flex">{t7}</span>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t5 || $[15] !== t8) {
    t9 = <PrimaryButton onClick={t4} disabled={t5}>{t6}{t8}</PrimaryButton>;
    $[14] = t5;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== blog || $[18] !== errors || $[19] !== localeData || $[20] !== onEditBlog || $[21] !== settingsOpened || $[22] !== t || $[23] !== userDomainsSelect) {
    t10 = blog && <EditModal caption={t("dashboard.blog.title")} toastCaption={t("dashboard.blog.blog_updated")} afterLeave={onModalClose} opened={settingsOpened} onClose={() => setSettingsOpened(false)} instance={blog} editable={[[{
      field: "name",
      onRender: (value, onChange) => <FormInput key="blog-name-edit" value={value} onChange={e => onChange(e.target.value)} error={errors.name} label={t("global.name")} />
    }, {
      field: "domain",
      onRender: (value_0, onChange_0) => <FormSelect withoutCloseIcon={true} isPreFetching={!userDomainsSelect} key="blog-domain-edit" onChange={value_1 => value_1 && onChange_0({
        id: value_1,
        domainName: ""
      })} value={value_0.id} label={t("dashboard.blog.domain_name")} data={userDomainsSelect} error={errors.domain} />
    }], {
      field: "path",
      onRender: (value_2, onChange_1, data_1) => {
        const domainName = userDomainsSelect?.find(el => {
          const domain = data_1.domain;
          return el.id === domain.id;
        })?.caption;
        return <div key="blog-path-edit-and-rss-feed" className="flex flex-col gap-5"><FormUrlPathInput label={t("global.url_path")} error={errors.path} value={value_2} onChange={e_0 => onChange_1(e_0.target.value)} domainName={domainName} /><FormUrlPathInput label={<div className="flex items-end gap-1.5">{t("dashboard.blog.rss_feed")}<RssIcon /></div>} domainName={domainName} value={`${data_1.path}${!!data_1.path.length ? "/" : ""}feed`} disabled={true} /></div>;
      }
    }, {
      field: "locale",
      onRender: (value_3, onChange_2) => <FormSelect withoutCloseIcon={true} key="blog-locale-edit" onChange={value_4 => value_4 && onChange_2(value_4)} value={value_3} label={t("global.language")} data={localeData} placeholder={t("dashboard.blog.choose_domain")} error={errors.locale} />
    }]} onEdit={data_2 => onEditBlog(data_2)} />;
    $[17] = blog;
    $[18] = errors;
    $[19] = localeData;
    $[20] = onEditBlog;
    $[21] = settingsOpened;
    $[22] = t;
    $[23] = userDomainsSelect;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
export default BlogSettings;